import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Copy, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  type LocationListItem,
  locationsGetAllMine,
  locationsGetAllMineWithMenus,
  menusCreate,
  menusDuplicate,
  menusGetAllMine,
} from "~/api-refs";
import { RadioPanels } from "~/components/shared/radio-panels";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/menus/new")({
  component: CreateMenuModal,
});

interface CreateMenuFormData {
  name: string;
  locations: Id<"locations">[];
  addToFutureLocations: boolean;
}

interface DuplicateMenuFormData {
  duplicateMenuId: string;
  name: string;
  locations: Id<"locations">[];
  addToFutureLocations: boolean;
}

function CreateMenuModal() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"init" | "new" | "duplicate">("init");
  const [createMenuOption, setCreateMenuOption] = useState<"new" | "duplicate">(
    "new"
  );

  // Fetch locations
  const { data: locations } = useQuery(convexQuery(locationsGetAllMine, {}));

  const handleClose = () => {
    // Navigate to empty if no menus, otherwise try to go back or to menus root
    void navigate({ to: "/menus/empty" });
  };

  const handleFinished = (menuId: string) => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const menuTypes = {
    init: {
      title: "Create Menu",
      description: "How do you want to create your menu?",
    },
    new: {
      title: "New Menu",
      description: "Menus can be shared across multiple locations",
    },
    duplicate: {
      title: "Duplicate Menu",
      description: "Duplicate all categories and items from an existing menu",
    },
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noScrollPrevention>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>{menuTypes[step].title}</DialogTitle>
          <DialogDescription className="text-base">
            {menuTypes[step].description}
          </DialogDescription>
        </DialogHeader>

        {step === "init" && (
          <>
            <RadioPanels
              items={[
                {
                  value: "new",
                  label: "New menu",
                  description: "Create a new menu from scratch",
                  icon: Plus,
                },
                {
                  value: "duplicate",
                  label: "Duplicate menu",
                  description: "Choose an existing menu to use as a template",
                  icon: Copy,
                },
              ]}
              onValueChange={setCreateMenuOption}
              orientation="vertical"
              value={createMenuOption}
            />
            <Button
              className="h-12 w-full"
              onClick={() => setStep(createMenuOption)}
              type="button"
            >
              Next
            </Button>
          </>
        )}
        {step === "new" && (
          <NewMenuForm
            locations={locations}
            onBack={() => setStep("init")}
            onFinished={handleFinished}
          />
        )}
        {step === "duplicate" && (
          <DuplicateMenuForm
            onBack={() => setStep("init")}
            onFinished={handleFinished}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewMenuForm({
  locations,
  onBack,
  onFinished,
}: {
  locations: LocationListItem[] | undefined;
  onBack: () => void;
  onFinished: (id: string) => void;
}) {
  const form = useForm<CreateMenuFormData>({
    defaultValues: {
      name: "",
      locations: [],
      addToFutureLocations: false,
    },
  });

  const createMenuMutation = useMutation({
    mutationFn: (data: {
      name: string;
      addToFutureLocations: boolean;
      locations: Id<"locations">[];
    }) => {
      return convexClient.mutation(menusCreate, data);
    },
  });

  const handleSubmit = async (values: CreateMenuFormData) => {
    if (createMenuMutation.isPending) {
      return;
    }
    try {
      const result = await createMenuMutation.mutateAsync({
        name: values.name,
        locations: values.locations,
        addToFutureLocations: values.locations.length === locations?.length,
      });
      // Track menu creation in PostHog
      ProductEvents.menuCreated(result.menuId);
      onFinished(result.menuId);
    } catch (error) {
      logger.exception("menu_create_failed", error);
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Menu name</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="locations"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Locations</FormLabel>
              <FormControl>
                <MultiCombobox
                  groups={["default"]}
                  allOption={{
                    dropdownLabel: "All and future locations",
                    selectedLabel: "All and future locations",
                  }}
                  groupLabels={{
                    default: "Your locations",
                  }}
                  items={{
                    default: locations
                      ? locations.map((r) => ({
                          value: r.id,
                          label: r.name,
                          imageUrl:
                            r.logoUrl ?? "/images/avatar-placeholder.svg",
                        }))
                      : [],
                  }}
                  onValueChange={(val) => field.onChange(val.default)}
                  values={{
                    default: field.value ?? [],
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full gap-3">
          <Button
            className="flex-1"
            onClick={onBack}
            size="lg"
            type="button"
            variant="secondary"
          >
            Back
          </Button>
          <Button
            className="flex-1"
            disabled={!form.formState.isValid || createMenuMutation.isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form>
  );
}

function DuplicateMenuForm({
  onBack,
  onFinished,
}: {
  onBack: () => void;
  onFinished: (id: string) => void;
}) {
  const [duplicateStep, setDuplicateStep] = useState(0);

  const { data: menusResult } = useQuery(
    convexQuery(menusGetAllMine, {
      paginationOpts: {
        numItems: 100,
        cursor: null,
      },
    })
  );
  const menus = menusResult?.page ?? [];

  const { data: locations } = useQuery(
    convexQuery(locationsGetAllMineWithMenus, {})
  );

  const duplicateMenuMutation = useMutation({
    mutationFn: (data: {
      id: Id<"menus">;
      name: string;
      locations: Id<"locations">[];
      addToFutureLocations: boolean;
    }) => {
      return convexClient.mutation(menusDuplicate, data);
    },
  });

  const form = useForm<DuplicateMenuFormData>({
    defaultValues: {
      duplicateMenuId: "",
      name: "",
      locations: [],
      addToFutureLocations: false,
    },
  });

  const formWatchDuplicateMenuId = form.watch("duplicateMenuId");

  const menuSelected = formWatchDuplicateMenuId
    ? menus.find((m) => m.id === formWatchDuplicateMenuId)
    : null;
  const locationsSelected = formWatchDuplicateMenuId
    ? (locations ?? []).filter((r) =>
        r.menuRelations.find((m) => m.menuId === formWatchDuplicateMenuId)
      )
    : [];

  const handleSubmit = async (values: DuplicateMenuFormData) => {
    if (duplicateMenuMutation.isPending) {
      return;
    }
    try {
      const result = await duplicateMenuMutation.mutateAsync({
        id: toConvexTableId("menus", values.duplicateMenuId),
        name: values.name,
        locations: values.locations,
        addToFutureLocations:
          values.locations.length === (locations?.length ?? 0),
      });
      // Track menu creation (via duplication) in PostHog
      ProductEvents.menuCreated(result.id);
      onFinished(result.id);
    } catch (error) {
      logger.exception("menu_duplicate_failed", error);
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
        {duplicateStep === 0 && (
          <>
            <FormField
              control={form.control}
              name="duplicateMenuId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Choose existing menu to duplicate</FormLabel>
                  <FormControl>
                    <Combobox
                      disabled={!menus.length}
                      dropdownClassName="w-130"
                      items={menus.map((m) => ({
                        value: m.id,
                        label: m.name,
                        keywords: [m.name],
                      }))}
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full justify-between space-x-3">
              <Button
                className="w-full"
                onClick={onBack}
                size="lg"
                type="button"
                variant="secondary"
              >
                Back
              </Button>
              <Button
                className="w-full"
                disabled={!menuSelected}
                onClick={(e) => {
                  e.preventDefault();
                  if (menuSelected) {
                    form.setValue("name", menuSelected.name);
                    form.setValue(
                      "addToFutureLocations",
                      menuSelected.addToFutureLocations
                    );
                    if (locationsSelected.length) {
                      form.setValue(
                        "locations",
                        locationsSelected.map((r) => r._id)
                      );
                    }
                    setDuplicateStep(1);
                  }
                }}
                size="lg"
                type="button"
              >
                Next
              </Button>
            </div>
          </>
        )}
        {duplicateStep === 1 && menuSelected && (
          <>
            <div className="bg-muted w-full space-y-1 rounded-2xl px-5 py-4">
              <div className="flex w-full justify-between">
                <p className="text-foreground">{menuSelected.name}</p>
                <div className="flex -space-x-5 transition-all duration-200 hover:space-x-1 lg:space-x-1">
                  <Badge variant="information">
                    <p>{menuSelected.categoriesCount} categories</p>
                  </Badge>
                  <Badge variant="secondary">
                    <p>{menuSelected.itemsCount} items</p>
                  </Badge>
                </div>
              </div>
              {menuSelected.addToFutureLocations && (
                <p className="body-sm text-muted-foreground leading-tight">
                  All and future locations
                </p>
              )}
            </div>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New menu name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="locations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Locations</FormLabel>
                  <FormControl>
                    <MultiCombobox
                      groups={["default"]}
                      allOption={{
                        dropdownLabel: "All and future locations",
                        selectedLabel: "All and future locations",
                      }}
                      groupLabels={{
                        default: "Your locations",
                      }}
                      items={{
                        default: (locations ?? []).map((r) => ({
                          value: r._id,
                          label: r.name,
                          imageUrl:
                            r.logoUrl ?? "/images/avatar-placeholder.svg",
                        })),
                      }}
                      onValueChange={(val) => field.onChange(val.default)}
                      values={{
                        default: field.value ?? [],
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full justify-between space-x-3">
              <Button
                className="w-full"
                onClick={() => setDuplicateStep(0)}
                size="lg"
                type="button"
                variant="secondary"
              >
                Back
              </Button>
              <Button
                className="w-full"
                disabled={
                  !form.formState.isValid || duplicateMenuMutation.isPending
                }
                size="lg"
                type="submit"
              >
                Create
              </Button>
            </div>
          </>
        )}
      </form>
    </Form>
  );
}
